import { ReactElement, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useQueryParams } from 'shared/hooks/use-query-params';

import CategoryFilters from 'ud-ui/components/category/components/list-wrapper/category-filters';
import CategoryList from 'ud-ui/components/category/components/list/category-list';
import EmptyList from 'ud-ui/components/category/components/list/empty-list';
import { FilterConfig, FilterValues, SortConfig, SortValue } from 'ud-ui/components/category/domain/types';
import { CategoryListContextProvider, CategoryListContextType } from 'ud-ui/components/category/hooks/use-category-list-context';
import ListPageWrapper from 'ud-ui/components/category/pages/list-page-wrapper';
import { getItemsOnPage, getPagesNum } from 'ud-ui/components/category/utils';
import { UDListItemProps } from 'ud-ui/components/list/components/list-item';
import { CategorySkeleton } from '../components/list/category-skeleton';

function CategoryListPage(props: IProps) {
  const {
    getFiltered,
    getNextPages,
    onCreateClick,
    children: items = [],
    isLoading,
    categoryName,
    text,
    filters,
    sortConfig,
    onFiltersChange,
    onSortChange,
    extraFilters,
  } = props;

  const isNewMode = filters !== undefined;

  const [params, setParams] = useQueryParams({
    page: {
      key: 'page',
      defaultValue: 1,
      parser: Number,
    },
    filter: {
      key: 'filter',
      defaultValue: '',
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const [storage, setStorage] = useLocalStorage({
    perPage: {
      key: `${categoryName}PerPage`,
      defaultValue: 10,
      parser: Number,
    },
  });

  useLayoutEffect(() => {
    if (!isNewMode) {
      getFiltered?.(params.filter);
    }
  }, [getFiltered, isNewMode, params.filter]);

  // Derived state for new mode
  const filterValues = useMemo<FilterValues>(() => {
    if (!filters) return {};
    return Object.fromEntries(
      filters.map((f) => {
        const raw = searchParams.get(f.key);
        if (f.type === 'select' && f.isMulti) {
          return [f.key, raw ? raw.split(',') : null];
        }
        return [f.key, raw || null];
      }),
    );
  }, [filters, searchParams]);

  const sortValue = useMemo<SortValue | undefined>(() => {
    if (!sortConfig) return undefined;
    const field = searchParams.get('sortField') ?? sortConfig.defaultField ?? '';
    const direction = (searchParams.get('sortDirection') ?? sortConfig.defaultDirection ?? 'asc') as 'asc' | 'desc';
    if (!field) return undefined;
    return { field, direction };
  }, [sortConfig, searchParams]);

  const totalPages = useMemo(() => {
    return getPagesNum(items.length, storage.perPage);
  }, [items.length, storage.perPage]);

  useEffect(() => {
    // totalPages is 0 while the list is still loading (or genuinely empty) — there's no
    // valid page range to clamp against yet, so skip it rather than stamping `page=1` into
    // the URL on every empty/loading list.
    if (totalPages > 0 && params.page > totalPages) {
      setParams({ page: totalPages });
    }
  }, [params.page, totalPages, setParams]);

  const onPageChange = useCallback(
    (page: number) => {
      setParams({ page });
      if (totalPages === page) {
        getNextPages?.();
      }
    },
    [getNextPages, setParams, totalPages],
  );

  // Legacy filter handler
  const onFilterChange = useCallback(
    (value: string) => {
      setParams({ filter: value, page: 1 });
      getFiltered?.(value);
    },
    [getFiltered, setParams],
  );

  // New mode filter handler
  const handleFilterChange = useCallback(
    (key: string, value: string | string[] | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          next.delete(key);
        } else if (Array.isArray(value)) {
          next.set(key, value.join(','));
        } else {
          next.set(key, value);
        }
        next.set('page', '1');
        return next;
      });
      if (onFiltersChange && filters) {
        const newValues: FilterValues = {};
        filters.forEach((f) => {
          if (f.key === key) {
            newValues[f.key] = value;
          } else {
            const raw = searchParams.get(f.key);
            if (f.type === 'select' && f.isMulti) {
              newValues[f.key] = raw ? raw.split(',') : null;
            } else {
              newValues[f.key] = raw || null;
            }
          }
        });
        onFiltersChange(newValues);
      }
    },
    [filters, onFiltersChange, searchParams, setSearchParams],
  );

  // New mode sort handler
  const handleSortChange = useCallback(
    (sort: SortValue) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('sortField', sort.field);
        next.set('sortDirection', sort.direction);
        next.set('page', '1');
        return next;
      });
      onSortChange?.(sort);
    },
    [onSortChange, setSearchParams],
  );

  const onPerPageChange = useCallback(
    (perPage: number) => {
      setStorage({ perPage });
    },
    [setStorage],
  );

  const isFilterActive = isNewMode ? Object.values(filterValues).some(Boolean) : Boolean(params.filter);

  const contextValue: CategoryListContextType = useMemo(() => {
    return {
      buttonProps: {
        text: text?.addButtonText ?? `Добавить`,
        variant: `dark`,
      },
      textProps: {
        title: isFilterActive ? 'Ничего не найдено' : `Список пуст`,
        subtitle: isFilterActive ? `Попробуйте изменить ваш поиск` : '',
      },
      styleProps: {
        containerClassName: 'p-4',
        textClassName: 'my-2',
        buttonClassName: 'mt-3',
      },
      isFilterActive,
      filter: isNewMode ? '' : params.filter,
      onCreateClick: onCreateClick,
    };
  }, [isFilterActive, isNewMode, onCreateClick, params.filter, text?.addButtonText]);

  const itemsOnPage = useMemo(() => {
    return getItemsOnPage(items, params.page, storage.perPage);
  }, [items, params.page, storage.perPage]);

  const renderContent = () => {
    if (isLoading) {
      return <CategorySkeleton count={storage.perPage} className="mb-2" />;
    }

    if (itemsOnPage.length === 0) {
      return <EmptyList />;
    }

    return <CategoryList items={itemsOnPage} styleProps={contextValue.styleProps} />;
  };

  const filterNode = isNewMode ? (
    <CategoryFilters
      filters={filters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      sortConfig={sortConfig}
      sortValue={sortValue}
      onSortChange={handleSortChange}
      extraFilters={extraFilters}
      perPage={storage.perPage}
      onChangePerPage={onPerPageChange}
    />
  ) : undefined;

  return (
    <ListPageWrapper
      pages={totalPages}
      currentPage={params.page}
      onPageChange={onPageChange}
      filter={filterNode}
      filterText={isNewMode ? undefined : params.filter}
      filterInputPlaceholder={text?.searchPlaceholder ?? `Поиск`}
      onFilterChange={isNewMode ? undefined : onFilterChange}
      perPage={storage.perPage}
      onChangePerPage={onPerPageChange}
    >
      <CategoryListContextProvider value={contextValue}>{renderContent()}</CategoryListContextProvider>
    </ListPageWrapper>
  );
}

export default CategoryListPage;

interface IProps {
  getNextPages?: () => void;
  getFiltered?: (value: string) => void;
  onCreateClick?: () => void;
  children?: ReactElement<UDListItemProps>[];
  isLoading?: boolean;

  categoryName: string;

  text?: {
    searchPlaceholder?: string;
    addButtonText?: string;
  };

  filters?: FilterConfig[];
  sortConfig?: SortConfig;
  onFiltersChange?: (values: FilterValues) => void;
  onSortChange?: (sort: SortValue) => void;
  extraFilters?: ReactNode;
}
